import {
  Button,
  Input,
  MinthrSelect,
  Sidebar,
  UsersBox,
} from 'components/common';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import {
  GetApprovalById,
  GetListApproval,
  removeApprovalLevelService,
  UpdateApprovalService,
} from 'features/settings/approval/approval/services/approvalService';
import { FieldArray, Form, Formik, FormikHelpers } from 'formik';
import useFetchData from 'hooks/useFetch';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import UserShortCut from 'components/common/UserShortCut';
import Tippy from '@tippyjs/react';
import { useDialog } from 'contexts/DialogContext';
import { ApprovalFormValues } from 'features/settings/approval/approval/types/approvalTypes';
import * as Yup from 'yup';
import MintErrorMessage from 'components/common/MintErrorMessage';

interface UpdateApprovalProps {
  id: number;
  refetch: () => void;
  type: string;
  Sidebartype?: string;
}
const UpdateApproval: React.FC<SideBarProps<UpdateApprovalProps>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { t } = useTranslation();
  const { closeSidebar } = useSidebarManager();

  const [showUsers, setShowUsers] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [notification, setNotification] = useState<boolean>();
  const dialog = useDialog();

  const { data: listApproval } = useFetchData(GetListApproval, [params.type]);
  const { data: approval } = useFetchData(GetApprovalById, [params.id]);

  const validationSchema = Yup.object().shape({
    fk_Type: approval?.canDeleted
      ? Yup.string().required(t('RequiredAttribute'))
      : Yup.string().notRequired(),
    levels: Yup.array().of(
      Yup.object().shape({
        levelType: Yup.string().required(t('RequiredAttribute')),
        fk_User: Yup.string().when('levelType', {
          is: 'SE',
          then: (schema) => schema.required(t('RequiredAttribute')),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    ),
  });

  const handelSubmit = async (
    values: ApprovalFormValues,
    { setStatus, setSubmitting }: FormikHelpers<ApprovalFormValues>,
  ): Promise<void> => {
    try {
      setSubmitting(true);
      const transformedLevels = values.levels.map((level) => ({
        ...level,
        fk_User: Array.isArray(level.fk_User)
          ? level.fk_User.map((user) => user.value || user).join(',')
          : level.fk_User || null,
      }));

      const payload = {
        ...values,
        levels: transformedLevels,
        usersNotification: values.usersNotification
          ? values.usersNotificationList?.map((u) => u.value).join(',') || ''
          : '',
        fk_Type: approval?.canDeleted ? values.fk_Type : approval?.approvalType,
      } as ApprovalFormValues;

      const response = await UpdateApprovalService(payload);
      if (response.success) {
        toast.success(t('Default.succesMessage'));
        params.refetch();
        closeSidebar(UpdateApproval);
      } else {
        setStatus(response.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const deleteApprovalLevelService = (
    id: number,
    index: number,
    remove: (index: number) => void,
  ): void => {
    if (id != 0) {
      dialog
        .delete('delete.confirm_title', 'Approval.confirm_description')
        .then(async (res) => {
          if (res) {
            const response = await removeApprovalLevelService(id);
            if (response.success) {
              toast.success(t('Default.succesMessage'));
              remove(index);
              params.refetch();
            } else {
              toast.error(response.message);
            }
          }
        });
    } else {
      remove(index);
    }
  };

  useEffect(() => {
    if (approval?.usersNotificationList) {
      setIds(approval.usersNotificationList.map((u) => u.value) as string[]);
      setNotification(approval.usersNotification ? true : false);
    }
  }, [approval]);

  if (!approval) return;

  return (
    <Sidebar
      size="m"
      type={params.Sidebartype ? 'secondary' : 'primary'}
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-4 minthr-border-b minthr-border-b-3">
        <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
          <img className="minthr-icon-m" src="/icons/sites.svg" alt="" />
        </div>
        <h3>
          {t('Approval.Edit.Title')}{' '}
          {t('ApprovalType.' + approval.approvalType)}
        </h3>

        <button
          onClick={() => {
            closeSidebar(UpdateApproval);
          }}
          className="minthr-sidebar-close"
          type="button"
        >
          <img src="/icons/close.svg" alt="" />
        </button>
      </div>
      <Formik
        initialValues={approval as ApprovalFormValues}
        onSubmit={handelSubmit}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue, isSubmitting, status }) => (
          <Form>
            {status && <MintErrorMessage message={status} />}

            {approval.canDeleted ? (
              <div className="row g-0 minthr-mb-5 minthr-pb-3 minthr-border-b">
                <div className="col-sm-12">
                  <MinthrSelect
                    name="fk_Type"
                    label={t('Approval.F.Fk_Type')}
                    options={
                      approval.fk_TypeList &&
                      JSON.parse(approval.fk_TypeList).map(
                        (item: SelectItem) => ({
                          label: item.text,
                          value: item.id,
                        }),
                      )
                    }
                    value={params.id}
                  />
                </div>
              </div>
            ) : (
              <div className="row g-0 minthr-mb-5 minthr-pb-3 minthr-border-b">
                <div className="col-sm-12">
                  <Input
                    name="approvalType"
                    label={t('Approval.F.Fk_Type')}
                    IsDisabled={true}
                    value={approval.approvalType}
                    active={true}
                    type="text"
                  />

                  <input type="hidden" />
                  <span></span>
                </div>
              </div>
            )}

            <div className="minthr-timeline">
              <FieldArray name="levels">
                {({ remove, push }) => (
                  <>
                    {values.levels.map((item, index) => (
                      <div
                        key={index}
                        className="minthr-timeline-item minthr-pb-5"
                      >
                        <div className="minthr-timeline-date-icon minthr-icon-bg minthr-icon-bg-s minthr-icon-circle minthr-bg-info-opacity minthr-mt-1">
                          <div className="h6 minthr-color-primary">
                            {index + 1}
                          </div>
                        </div>
                        <div className="minthr-timeline-content minthr-pl-3">
                          <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between">
                            <MinthrSelect
                              name={`levels.${index}.levelType`}
                              label={
                                t('Approval.F.LevelType') + ' ' + (index + 1)
                              }
                              options={
                                approval.levelTypeList &&
                                JSON.parse(
                                  approval.levelTypeList as unknown as string,
                                ).map((item: SelectItem) => ({
                                  label: t(item.text as string),
                                  value: item.id,
                                }))
                              }
                            />
                            {index != 0 && (
                              <button
                                onClick={() => {
                                  if (item.id) {
                                    deleteApprovalLevelService(
                                      item.id,
                                      index,
                                      remove,
                                    );
                                  } else {
                                    remove(index);
                                  }
                                }}
                                type="button"
                                className="minthr-btn minthr-btn-s minthr-btn-icon-origin minthr-icon-danger minthr-px-1 minthr-ml-1"
                              >
                                <img src="/icons/delete.svg" alt="" />
                              </button>
                            )}
                          </div>
                        </div>
                        {values.levels[index]?.levelType == 'SE' && (
                          <div className="minthr-timeline-content minthr-pl-3 minthr-mt-5  div_semp">
                            <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between">
                              <MinthrSelect
                                name={`levels.${index}.fk_User`}
                                label={t('Approval.F.Fk_User')}
                                options={
                                  listApproval
                                    ? listApproval.map((item) => ({
                                        label: item.text,
                                        value: item.id,
                                      }))
                                    : []
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    <div
                      className="minthr-timeline-item minthr-pb-5"
                      id="new-btn"
                    >
                      <div className="minthr-timeline-date-icon minthr-icon-bg minthr-icon-bg-s minthr-icon-circle minthr-bg-info-opacity minthr-mt-1 minthr-text-hide">
                        00
                      </div>
                      <div className="minthr-timeline-content minthr-pl-3">
                        <button
                          onClick={() => push({ levelType: '', fk_User: '' })}
                          type="button"
                          id="addNew"
                          className="minthr-flex minthr-flex-items-center minthr-flex-justify-center minthr-border minthr-border-primary minthr-border-dashed minthr-border-rounded minthr-bg-primary-opacity minthr-p-2 minthr-w-90 minthr-hover-success-opacity"
                        >
                          <div className="minthr-flex minthr-flex-items-center">
                            <img
                              className="minthr-icon-l minthr-mr-2"
                              src="/icons/plus.svg"
                              alt=""
                            />
                            <div className="minthr-color-primary">
                              {t('add')}
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </FieldArray>
            </div>

            <div className="minthr-check-switch-container minthr-py-3 minthr-px-1 minthr-mb-2 minthr-bg-white">
              <div className="minthr-flex">
                <label className="minthr-check-switch">
                  <input
                    type="checkbox"
                    id="notifie-users"
                    checked={notification}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setFieldValue('usersNotification', enabled);
                      setNotification(enabled);
                    }}
                  />
                  <span className="slider"></span>
                </label>
                <h5 className="minthr-ml-3">
                  {t('approval.activate.notifications')}
                </h5>
              </div>

              <div className="minthr-pt-2">
                {values.usersNotification ? (
                  <>
                    <p className="text-s minthr-color-gray">
                      {t('approval.notifie.description')}
                    </p>
                    <Tippy
                      content={
                        <div style={{ width: 'calc(var(--unit-minthr)* 380)' }}>
                          <UsersBox
                            url={'/api/approval/GetUsersScroll'}
                            ids={ids}
                            setIds={setIds}
                          />
                        </div>
                      }
                      trigger="click"
                      interactive={true}
                      arrow={false}
                      className="transparent-background"
                      visible={showUsers}
                    >
                      <div className="minthr-flex minthr-flex-items-center minthr-w-100">
                        {values.usersNotificationList &&
                        values.usersNotificationList.length > 0 ? (
                          <div
                            id="imagesContainer"
                            className="minthr-flex minthr-flex-items-center minthr-bg-light-primary minthr-p-3 minthr-mt-2 minthr-mb-2 minthr-w-100"
                            style={{ overflowX: 'auto' }}
                          >
                            <button
                              onClick={() => {
                                setShowUsers(!showUsers);
                              }}
                              type="button"
                              className="minthr-bg-primary-opacity minthr-icon-bg minthr-icon-bg-l minthr-icon-circle minthr-mr-3 minthr-border-none"
                              style={{ rotate: `${showUsers ? '45deg' : ''}` }}
                            >
                              <img
                                className="minthr-icon-l"
                                src="/icons/plus.svg"
                                alt=""
                              />
                            </button>

                            {values.usersNotificationList.map((user, i) => (
                              <div
                                key={i}
                                className="minthr-avatar minthr-ml-0 minthr-mr-3 user-item"
                              >
                                <button
                                  className="minthr-avatar-delete remove-user"
                                  onClick={() => {
                                    if (values.usersNotificationList) {
                                      setFieldValue(
                                        'usersNotificationList',
                                        values.usersNotificationList.filter(
                                          (item) => item.value != user.value,
                                        ),
                                      );
                                    }

                                    setIds((items) =>
                                      items.filter(
                                        (item) => item !== user.value,
                                      ),
                                    );
                                  }}
                                  type="button"
                                >
                                  <img src="/icons/close.svg" alt="" />
                                </button>
                                <Tippy content={user.text} placement="right">
                                  <UserShortCut
                                    className="minthr-avatar minthr-avatar-s minthr-border-none"
                                    src={`upload/profileimages/${user.value}/${user.imagePath}`}
                                    alt=""
                                  />
                                </Tippy>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setShowUsers(!showUsers);
                            }}
                            type="button"
                            className="minthr-flex minthr-flex-items-center minthr-flex-justify-center minthr-bg-light-primary minthr-p-3 minthr-w-100 minthr-border-none add-user"
                          >
                            <div className="minthr-flex minthr-flex-items-center minthr-flex-justify-center">
                              <div className="minthr-bg-primary-opacity minthr-icon-bg minthr-icon-bg-l minthr-icon-circle">
                                <img
                                  className="minthr-icon-l"
                                  src="/icons/plus.svg"
                                  alt=""
                                  style={{
                                    rotate: `${showUsers ? '45deg' : ''}`,
                                  }}
                                />
                              </div>

                              <div className="minthr-color-primary minthr-ml-2">
                                {t('approval.add.members')}
                              </div>
                            </div>
                          </button>
                        )}
                      </div>
                    </Tippy>
                  </>
                ) : null}
              </div>
            </div>

            <div className="minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-py-4 minthr-mx-n5 minthr-px-4 minthr-mt-auto">
              <Button
                loading={isSubmitting}
                type="submit"
                className="minthr-btn minthr-btn-primary minthr-ml-auto"
              >
                {t('submit')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Sidebar>
  );
};

export default UpdateApproval;
